/**
 * The space glue (M2 plan §2.3, §2.7, §6 M2-04): builds the `SpaceWorld` (the shared context and every runtime,
 * in the order the frozen contract names: stations → docking → rocks → salvage → encounters → tools), registers the
 * runtimes' stage methods on the sim in the §2.3 order plus the game clock, and declares the `m2` service.
 *
 * Stage order (registration order within each stage):
 * - control: tools.control → docking.control
 * - tools:   rocks.tools → salvage.tools
 * - physics: rocks.physics → salvage.physics → docking.physics
 * - deaths:  salvage.deaths → encounters.deaths → tools.deaths
 * - end:     encounters.end → salvage.end → docking.end → the game clock (`mirror.gameTime += SIM_DT`)
 *
 * Built once per career session (not per frame); the stage methods themselves are allocation-free (D-014).
 */
import type { GameContext } from '../app/feature'
import type { LoadedModel } from '../assets/loader'
import { SIM_DT } from '../core/loop'
import type { GameData } from '../data'
import type { M2Data } from '../data/m2Schemas'
import { registerSimStage, type Sim, type SimStage } from '../sim'
import { createSpaceContext, type CareerMirror, type M2Runtime, type SpaceSubsystem, type SpaceWorld } from './contracts'
import { createDockingRuntime } from './docking'
import { createEncountersRuntime } from './encounters'
import { createRocksRuntime } from './rocks'
import { createSalvageRuntime } from './salvage'
import { createStationsRuntime } from './stations'
import { createToolsRuntime } from './tools'

declare module '../app/services' {
  interface ServiceMap {
    /** The M2 runtime (career mode only), provided by the career feature (M2-22). */
    m2: M2Runtime
  }
}

/**
 * Builds the space world for a career: the context (event ring, player input and tool state, mirror, the M2 RNG
 * seeded from `careerSeed`) and every runtime. Registers nothing; `registerSpaceStages` wires it into the sim.
 * `models` is the live map of loaded models (the deferred station and prop models appear in it as they load).
 */
export function createSpaceWorld(sim: Sim, game: GameData, data: M2Data, models: ReadonlyMap<string, LoadedModel>, careerSeed: number): SpaceWorld {
  const ctx = createSpaceContext(sim, game, data, models, careerSeed)
  const stations = createStationsRuntime(ctx)
  const docking = createDockingRuntime(ctx, stations)
  const rocks = createRocksRuntime(ctx)
  const salvage = createSalvageRuntime(ctx)
  const encounters = createEncountersRuntime(ctx, stations, docking)
  const tools = createToolsRuntime(ctx, { stations, docking, rocks, salvage })
  return {
    sim: ctx.sim,
    game: ctx.game,
    data: ctx.data,
    rng: ctx.rng,
    events: ctx.events,
    input: ctx.input,
    player: ctx.player,
    mirror: ctx.mirror,
    models: ctx.models,
    stations,
    docking,
    tools,
    rocks,
    salvage,
    encounters,
  }
}

/** One tick of in-space game time (the last function of the `end` stage). Allocation-free. */
function tickGameClock(mirror: CareerMirror): void {
  mirror.gameTime += SIM_DT
}

/**
 * The stage registrations of `space`, in §2.3 order: [stage, function] pairs. Exposed for tests and the dev
 * panel; `registerSpaceStages` registers exactly these.
 */
export function spaceStagePlan(space: SpaceWorld, clock: SpaceSubsystem): readonly (readonly [SimStage, SpaceSubsystem])[] {
  return [
    ['control', space.tools.control],
    ['control', space.docking.control],
    ['tools', space.rocks.tools],
    ['tools', space.salvage.tools],
    ['physics', space.rocks.physics],
    ['physics', space.salvage.physics],
    ['physics', space.docking.physics],
    ['deaths', space.salvage.deaths],
    ['deaths', space.encounters.deaths],
    ['deaths', space.tools.deaths],
    ['end', space.encounters.end],
    ['end', space.salvage.end],
    ['end', space.docking.end],
    ['end', clock],
  ]
}

/**
 * Registers the runtimes' stage methods on `sim` in the §2.3 order, with the game clock last in `end`, and returns
 * the function that unregisters all of them (call it between ticks, e.g. when a career ends).
 */
export function registerSpaceStages(sim: Sim, space: SpaceWorld): () => void {
  const mirror = space.mirror
  const clock: SpaceSubsystem = () => tickGameClock(mirror)
  const plan = spaceStagePlan(space, clock)
  const off: (() => void)[] = []
  for (const [stage, fn] of plan) off.push(registerSimStage(sim, stage, fn))
  return (): void => {
    for (let i = off.length - 1; i >= 0; i--) off[i]()
    off.length = 0
  }
}

/** The `m2` service, or null outside career mode (or before the career feature provided it). */
export function getM2(ctx: Pick<GameContext, 'mode' | 'services'>): M2Runtime | null {
  if (ctx.mode !== 'career') return null
  return ctx.services.maybe('m2') ?? null
}
